import { CSSProperties, ElementType, HTMLAttributes } from 'react'
import { cn } from './cn'
import styles from './Text.module.css'

type TextVariant =
  | 'heading-xl' | 'heading-lg' | 'heading-md' | 'heading-sm'
  | 'body-lg' | 'body-md' | 'body-sm'
  | 'caption' | 'label'

type TextColor = 'default' | 'secondary' | 'tertiary' | 'inverse' | 'primary' | 'danger' | 'success'

const defaultTag: Record<TextVariant, ElementType> = {
  'heading-xl': 'h1',
  'heading-lg': 'h2',
  'heading-md': 'h3',
  'heading-sm': 'h4',
  'body-lg': 'p',
  'body-md': 'p',
  'body-sm': 'p',
  'caption': 'span',
  'label': 'label',
}

export interface TextProps extends HTMLAttributes<HTMLElement> {
  variant?: TextVariant
  color?: TextColor
  weight?: 'normal' | 'medium' | 'semibold' | 'bold'
  align?: 'left' | 'center' | 'right'
  as?: ElementType
  truncate?: boolean
  lineClamp?: number
}

export function Text({
  variant = 'body-md',
  color,
  weight,
  align,
  as,
  truncate,
  lineClamp,
  className,
  style,
  ...props
}: TextProps) {
  const Tag = as ?? defaultTag[variant]
  const variantClass = variant.replace('-', '') as string

  const combinedStyle: CSSProperties | undefined =
    lineClamp != null
      ? { ...style, WebkitLineClamp: lineClamp }
      : style

  return (
    <Tag
      className={cn(
        styles[variantClass],
        color && styles[`color${color.charAt(0).toUpperCase()}${color.slice(1)}`],
        weight && styles[`weight${weight.charAt(0).toUpperCase()}${weight.slice(1)}`],
        align && styles[`align${align.charAt(0).toUpperCase()}${align.slice(1)}`],
        truncate && styles.truncate,
        lineClamp != null && styles.lineClamp,
        className,
      )}
      style={combinedStyle}
      {...props}
    />
  )
}
